'use client';

import { memo, useCallback, useState } from 'react';
import { Paper, Box, Typography, IconButton, Tooltip, CircularProgress, alpha } from '@mui/material';
import { DeleteForever, Notes, CheckCircle, Error as ErrorIcon, CloudUpload } from '@mui/icons-material';
import { UploadFile } from "@/app/lib/generated/graphql-types";
import { PendingUpload } from "@/app/features/shared/media-browser-v2/types/types";
import { renderMedia } from "@/app/features/shared/media-browser/media-renderer";
import { mediaRefName, mediaRefCaption, mediaRefType } from "@/app/features/shared/__to_be_integrated/types/general/media/media-helper";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";

type MediaGridItemProps = {
    media: UploadFile | PendingUpload;
    isSelected: boolean;
    onSelect: (id: string) => void;
    onDelete?: (media: UploadFile | PendingUpload) => void;
    onUpload?: (id: string) => void;
};

export const MediaGridItem = memo(function MediaGridItem({
    media,
    isSelected,
    onSelect,
    onDelete,
    onUpload,
}: MediaGridItemProps) {
    const [isImageLoading, setIsImageLoading] = useState(true);

    const isPending = 'uploadStatus' in media;
    const uploadStatus = isPending ? media.uploadStatus : null;

    const mediaType = mediaRefType(media);
    const name = mediaRefName(media);
    const hasCaption = !!mediaRefCaption(media);

    const handleLoad = useCallback(() => {
        setIsImageLoading(false);
    }, []);

    const handleSelect = useCallback(() => {
        const id = 'documentId' in media ? media.documentId : media.id;
        onSelect(id);
    }, [onSelect, media]);

    const handleDelete = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (onDelete) {
            onDelete(media);
        }
    }, [onDelete, media]);

    const handleUpload = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (onUpload && isPending) {
            onUpload(media.id);
        }
    }, [onUpload, isPending, media]);

    return (
        <Paper
            className="media-grid-item relative flex flex-col items-center overflow-hidden aspect-[3/4]"
            sx={{
                backgroundColor: 'background.default',
                contain: 'strict',
                ...(isSelected ? {
                    outline: (theme) => `3px solid ${theme.palette.primary.main}`,
                    boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
                } : {}),
                transition: 'outline 0.1s ease, box-shadow 0.1s ease',
                cursor: 'pointer',
                '&:hover': {
                    outline: (theme) => isSelected
                        ? `3px solid ${theme.palette.primary.main}`
                        : `2px solid ${theme.palette.primary.light}`,
                }
            }}
            onClick={handleSelect}
        >
            {/* Media Content */}
            <div className="flex flex-auto size-full relative">
                {isImageLoading && mediaType === 'image' && (
                    <Box className="absolute inset-0 flex items-center justify-center">
                        <LoadingSpinner />
                    </Box>
                )}
                {renderMedia(media, handleLoad)}

                {/* Upload Status Overlay */}
                {isPending && (
                    <Box
                        className="absolute inset-0 flex items-center justify-center"
                        sx={{
                            backgroundColor: alpha('#000', uploadStatus === 'uploading' ? 0.6 : 0.3),
                            transition: 'background-color 0.2s ease',
                        }}
                    >
                        {uploadStatus === 'pending' && (
                            <Tooltip title="Jetzt hochladen">
                                <IconButton
                                    onClick={handleUpload}
                                    className="size-14"
                                    sx={{
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: 'primary.dark',
                                        },
                                    }}
                                >
                                    <CloudUpload fontSize="large" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {uploadStatus === 'uploading' && (
                            <CircularProgress size={48} sx={{ color: 'white' }} />
                        )}
                        {uploadStatus === 'success' && (
                            <CheckCircle sx={{ fontSize: 48, color: 'success.main' }} />
                        )}
                        {uploadStatus === 'error' && (
                            <ErrorIcon sx={{ fontSize: 48, color: 'error.main' }} />
                        )}
                    </Box>
                )}

                {/* Delete Button */}
                {!isPending && onDelete && (
                    <Box
                        className="absolute right-2 top-2"
                        sx={{
                            opacity: 0,
                            transition: 'opacity 0.2s ease',
                            '.media-grid-item:hover &': {
                                opacity: 1,
                            },
                        }}
                    >
                        <IconButton
                            onClick={handleDelete}
                            className="size-10"
                            sx={{
                                backgroundColor: alpha('#000', 0.7),
                                color: 'error.light',
                                '&:hover': {
                                    backgroundColor: 'error.main',
                                    color: 'white',
                                },
                            }}
                        >
                            <DeleteForever />
                        </IconButton>
                    </Box>
                )}

                {/* Caption Indicator */}
                {hasCaption && (
                    <Box
                        className="absolute left-2 top-2 flex size-8 items-center justify-center rounded"
                        sx={{
                            backgroundColor: 'color-mix(in srgb, var(--background-darkest) 90%, transparent)',
                        }}
                    >
                        <Tooltip title="Bildunterschrift vorhanden">
                            <Notes fontSize="small" color="primary" />
                        </Tooltip>
                    </Box>
                )}
            </div>

            {/* Name Footer */}
            <Box
                className="w-full px-4 py-3"
                sx={{
                    backgroundColor: isSelected ? 'color-mix(in srgb, var(--primary) 90%, transparent)' : 'color-mix(in srgb, var(--background) 30%, transparent)',
                    backdropFilter: 'blur(8px)',
                    transition: 'background-color 0.2s ease',
                }}
            >
                <Typography
                    variant="body2"
                    color="white"
                    noWrap
                    sx={{
                        fontWeight: isSelected ? 600 : 400,
                    }}
                >
                    {name}
                </Typography>
            </Box>
        </Paper>
    );
});
